import { useMemo, useState } from 'react'
import { Badge, Button, Empty, Popover, Progress, Space, Tabs, Typography, message } from 'antd'
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  CloudUploadOutlined,
  FolderOutlined,
  ReloadOutlined,
} from '@ant-design/icons'
import {
  clearFinishedUploads,
  removeUploadTask,
  retryFailedUploads,
  retryUploadTask,
  useUploadQueue,
  type UploadTask,
} from '../services/knowledgeUploadQueue'
import './TransferManager.css'

function taskStatusLabel(status: UploadTask['status']) {
  switch (status) {
    case 'pending':
      return '等待中'
    case 'uploading':
      return '上传中'
    case 'done':
      return '已完成'
    case 'error':
      return '失败'
    default:
      return status
  }
}

function TaskRow({ task }: { task: UploadTask }) {
  const isActive = task.status === 'uploading'

  return (
    <li className={`transfer-task-row transfer-task-row--${task.status}`}>
      {task.relativePath ? (
        <FolderOutlined className="transfer-task-icon" />
      ) : (
        <CloudUploadOutlined className="transfer-task-icon" />
      )}
      <div className="transfer-task-main">
        <Typography.Text className="transfer-task-name" ellipsis={{ tooltip: task.displayName }}>
          {task.displayName}
        </Typography.Text>
        <span className="transfer-task-meta">
          {task.batchLabel} · {taskStatusLabel(task.status)}
        </span>
        {isActive && (
          <Progress
            percent={task.percent}
            size="small"
            status="active"
            showInfo={task.status === 'uploading'}
          />
        )}
        {task.status === 'error' && (
          <Typography.Text type="danger" className="transfer-task-error" ellipsis={{ tooltip: task.error }}>
            {task.error}
          </Typography.Text>
        )}
      </div>
      <Space size={4} className="transfer-task-actions">
        {task.status === 'error' && (
          <Button type="link" size="small" icon={<ReloadOutlined />} onClick={() => retryUploadTask(task.id)}>
            重试
          </Button>
        )}
        {(task.status === 'done' || task.status === 'error') && (
          <Button type="link" size="small" danger onClick={() => removeUploadTask(task.id)}>
            移除
          </Button>
        )}
      </Space>
    </li>
  )
}

function TransferPanel() {
  const tasks = useUploadQueue()
  const stats = useMemo(
    () => ({
      active: tasks.filter((t) => t.status === 'pending' || t.status === 'uploading').length,
      failed: tasks.filter((t) => t.status === 'error').length,
      done: tasks.filter((t) => t.status === 'done').length,
    }),
    [tasks],
  )
  const [tab, setTab] = useState<'active' | 'failed' | 'done'>('active')

  const filtered = useMemo(() => {
    if (tab === 'active') {
      return tasks.filter((t) => t.status === 'pending' || t.status === 'uploading')
    }
    if (tab === 'failed') return tasks.filter((t) => t.status === 'error')
    return tasks.filter((t) => t.status === 'done')
  }, [tasks, tab])

  const tabItems = [
    { key: 'active', label: `传输中 (${stats.active})` },
    { key: 'failed', label: `失败 (${stats.failed})` },
    { key: 'done', label: `已完成 (${stats.done})` },
  ]

  return (
    <div className="transfer-panel">
      <div className="transfer-panel-head">
        <Typography.Text strong>传输管理</Typography.Text>
        <Space size={8}>
          {stats.failed > 0 && (
            <Button
              type="link"
              size="small"
              icon={<ReloadOutlined />}
              onClick={() => {
                retryFailedUploads()
                message.info('已重新加入失败任务')
              }}
            >
              全部重试
            </Button>
          )}
          {stats.done > 0 && (
            <Button type="link" size="small" onClick={() => clearFinishedUploads()}>
              清空已完成
            </Button>
          )}
        </Space>
      </div>
      <Tabs
        size="small"
        activeKey={tab}
        onChange={(k) => setTab(k as typeof tab)}
        items={tabItems}
      />
      {filtered.length === 0 ? (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={tab === 'active' ? '暂无进行中的传输' : tab === 'failed' ? '暂无失败任务' : '暂无已完成记录'}
        />
      ) : (
        <ul className="transfer-task-list">
          {filtered.map((task) => (
            <TaskRow key={task.id} task={task} />
          ))}
        </ul>
      )}
    </div>
  )
}

export function TransferManager() {
  const tasks = useUploadQueue()
  const stats = useMemo(
    () => ({
      active: tasks.filter((t) => t.status === 'pending' || t.status === 'uploading').length,
      failed: tasks.filter((t) => t.status === 'error').length,
      done: tasks.filter((t) => t.status === 'done').length,
    }),
    [tasks],
  )
  const badgeCount = stats.active + stats.failed
  const hasAttention = stats.active > 0 || stats.failed > 0

  return (
    <Popover
      trigger="click"
      placement="bottomRight"
      content={<TransferPanel />}
      overlayClassName="transfer-manager-popover"
    >
      <Badge count={badgeCount > 0 ? badgeCount : undefined} size="small" offset={[-2, 2]}>
        <span
          className={`public-topbar-icon transfer-manager-trigger${hasAttention ? ' transfer-manager-trigger--active' : ''}`}
          title="传输管理"
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') e.currentTarget.click()
          }}
        >
          {stats.failed > 0 ? (
            <CloseCircleOutlined style={{ color: '#ff4d4f' }} />
          ) : stats.active > 0 ? (
            <CloudUploadOutlined style={{ color: 'var(--primary)' }} />
          ) : stats.done > 0 ? (
            <CheckCircleOutlined />
          ) : (
            <CloudUploadOutlined />
          )}
        </span>
      </Badge>
    </Popover>
  )
}
